import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  Typography,
  CircularProgress,
  Chip,
  Breadcrumbs,
} from "@mui/material";
import Rating from "@mui/material/Rating";
import StarIcon from "@mui/icons-material/Star";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { getAllProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const TopRatedPage = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

    useEffect(() => {
      document.title = 'DIDA MAX — Top Rated';
    }, []);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MAX_ITEMS = 12;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAllProducts();
        if (res && res.success && Array.isArray(res.products)) {
          const flat = [];
          res.products.forEach((entry) => {
            if (Array.isArray(entry.products)) flat.push(...entry.products);
          });

          const ratingsScore = (counts = {}) =>
            [1, 2, 3, 4, 5].reduce((s, r) => s + (Number(counts[String(r)] || 0) * r), 0);

          flat.sort((a, b) => {
            const aAvg = a.reviews_summary?.average_rating || 0;
            const bAvg = b.reviews_summary?.average_rating || 0;
            if (bAvg !== aAvg) return bAvg - aAvg;

            const aTotal = a.reviews_summary?.total_reviews || 0;
            const bTotal = b.reviews_summary?.total_reviews || 0;
            if (bTotal !== aTotal) return bTotal - aTotal;

            const aScore = ratingsScore(a.reviews_summary?.ratings_count);
            const bScore = ratingsScore(b.reviews_summary?.ratings_count);
            return bScore - aScore;
          });

          setProducts(flat.slice(0, MAX_ITEMS));
        } else {
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");

  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback
        ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null }
        : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="error" variant="h6" fontWeight={600}>
          {t("error_loading_top_rated", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الأعلى تقييماً" : "Error loading top rated" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background:
            "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />}
          sx={{ mb: 4 }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <HomeIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {lang === "ar" ? "الرئيسية" : "Home"}
            </Typography>
          </Link>
          <Typography variant="body2" color="text.primary" fontWeight={700}>
            {t("top_rated_title", { defaultValue: lang === "ar" ? "الأعلى تقييماً" : "Top Rated" })}
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 6,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              px: 3,
              py: 1.5,
              borderRadius: 50,
              background:
                "linear-gradient(135deg, rgba(213, 170, 47, 0.1) 0%, rgba(230, 199, 106, 0.1) 100%)",
              border: "1px solid rgba(213, 170, 47, 0.2)",
            }}
          >
            <EmojiEventsIcon sx={{ color: "primary.main", fontSize: 32 }} />
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                background:
                  "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("top_rated_title", { defaultValue: lang === "ar" ? "الأعلى تقييماً" : "Top Rated Products" })}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, lineHeight: 1.8 }}
          >
            {lang === "ar"
              ? "اكتشف منتجاتنا الأعلى تقييماً من قبل عملائنا المميزين"
              : "Discover our highest-rated products from our valued customers"}
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {products.map((product, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                    "& .product-image": {
                      transform: "scale(1.1)",
                    },
                    "& .rank-badge": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                  },
                  animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
                  "@keyframes fadeInUp": {
                    from: {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <CardActionArea
                  component={Link}
                  to={`/product/${product.id}`}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  {/* Rank Badge */}
                  {index < 3 && (
                    <Chip
                      icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                      label={`#${index + 1}`}
                      className="rank-badge"
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        zIndex: 2,
                        bgcolor: index === 0 ? "#FFD700" : index === 1 ? "#C0C0C0" : "#CD7F32",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.85rem",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        transition: "all 0.3s ease",
                      }}
                    />
                  )}

                  {/* Product Image */}
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      height: 240,
                      bgcolor: "grey.50",
                    }}
                  >
                    <Box
                      component="img"
                      src={getImage(product)}
                      alt={getName(product)}
                      className="product-image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)",
                      }}
                    />
                  </Box>

                  {/* Product Info */}
                  <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 220 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{
                        mb: 1.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.4,
                        height: "2.8em",
                      }}
                    >
                      {getName(product)}
                    </Typography>

                    {/* Rating Section */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(213, 170, 47, 0.05)",
                        border: "1px solid rgba(213, 170, 47, 0.1)",
                      }}
                    >
                      <Rating
                        value={product.reviews_summary?.average_rating || 0}
                        precision={0.1}
                        readOnly
                        size="small"
                        sx={{
                          "& .MuiRating-iconFilled": {
                            color: "primary.main",
                          },
                        }}
                      />
                      <Typography variant="h6" fontWeight={800} color="primary.main">
                        {product.reviews_summary?.average_rating?.toFixed(1) || "0.0"}
                      </Typography>
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 2 }}
                    >
                      <StarIcon sx={{ fontSize: 14, color: "primary.main", verticalAlign: "middle", mr: 0.5 }} />
                      ({product.reviews_summary?.total_reviews || 0} {t("reviews", { defaultValue: lang === "ar" ? "تقييم" : "reviews" })})
                    </Typography>

                    {/* Price Section */}
                    {(() => {
                      const pinfo = getPriceInfo(product);
                      if (!pinfo) return null;
                      const orig = formatPrice(pinfo.price);
                      const disc = formatPrice(pinfo.price_after_discount);
                      return (
                        <Box
                          sx={{
                            mt: "auto",
                            pt: 2,
                            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          {disc ? (
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "text.disabled",
                                  fontWeight: 600,
                                  mb: 0.5,
                                }}
                              >
                                {orig} {lang === "ar" ? "ج.م" : "EGP"}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 800,
                                  }}
                                >
                                  {disc} {lang === "ar" ? "ج.م" : "EGP"}
                                </Typography>
                                <Chip
                                  label={`-${Math.round((1 - parseFloat(disc) / parseFloat(orig)) * 100)}%`}
                                  size="small"
                                  sx={{
                                    bgcolor: "error.main",
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    height: 22,
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              variant="h5"
                              sx={{
                                color: "primary.main",
                                fontWeight: 800,
                              }}
                            >
                              {orig} {lang === "ar" ? "ج.م" : "EGP"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {products.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
            }}
          >
            <EmojiEventsIcon
              sx={{
                fontSize: 80,
                color: "grey.300",
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              {lang === "ar"
                ? "لا توجد منتجات عالية التقييم حالياً"
                : "No highly rated products available"}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TopRatedPage;
